// Helpers for the Routes tab's local↔UTC time conversion. Lives in its
// own file so `DeviceRoutesTab.tsx` stays "component-only" — keeps Vite's
// fast-refresh / react-refresh ESLint rule happy.

/**
 * Convert a `<input type="datetime-local">` value to absolute ms-since-epoch.
 *
 * `datetime-local` values are bare "YYYY-MM-DDTHH:MM" strings with no
 * timezone, and the browser parses them as **local time** per the HTML
 * spec. `Date.getTime()` then returns the corresponding ms-since-UTC-epoch.
 *
 * So if an operator in PDT (UTC-7) picks "2026-05-22T10:00", this returns
 * the epoch ms for 17:00:00 UTC on that date — which is what the backend
 * forwards to KairosDB's `text/state?timestamp=...` (which is documented
 * as absolute ms-since-epoch). The round-trip is pinned in
 * `__tests__/DeviceRoutesTab.test.ts`.
 */
export function datetimeLocalToMs(value: string): number | null {
  if (!value) return null;
  const ts = new Date(value).getTime();
  return Number.isFinite(ts) ? ts : null;
}

/**
 * Format an absolute ms timestamp as an ISO-8601 UTC string. Used to
 * surface the UTC equivalent of the local-time picker so operators can
 * confirm what's actually being sent to the backend.
 */
export function msToUtcIso(ms: number): string {
  return new Date(ms).toISOString();
}

/**
 * Format an absolute ms timestamp as a `datetime-local` input value
 * ("YYYY-MM-DDTHH:MM" in the user's local timezone). The input element
 * is minute-precision only, so seconds and milliseconds are dropped
 * from the displayed value — but the underlying ms is the source of
 * truth and is what we send to the backend.
 */
export function msToDatetimeLocal(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number): string => String(n).padStart(2, '0');
  const year = String(d.getFullYear());
  return (
    `${year}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/**
 * Default starting point for the historic-mode picker: 1 hour ago.
 * Returned as ms so the caller can keep ms as the single source of
 * truth (see `msToDatetimeLocal` for how the picker displays it).
 */
export function defaultHistoricMs(): number {
  return Date.now() - 60 * 60 * 1000;
}
